import { useCallback } from "react";
import {
  usePaginatedResource,
  PaginatedFetcher,
  pageFrom,
} from "./usePaginatedResource";
import { useEntityOperations } from "./useEntityOperations";
import { CommunityService } from "../services/communityService";
import {
  Comment,
  CreateCommentInput,
  UpdateCommentInput,
} from "../types/community";

export const useComments = (postId: number | null) => {
  const fetcher = useCallback<PaginatedFetcher<Comment, {}>>(
    async (page) => {
      const response = await CommunityService.getPostComments(postId!, page);
      if (!response) return null;

      const comments = response.data.map((comment) => ({
        ...comment,
        likes_count:
          typeof comment.likes_count === "number" ? comment.likes_count : 0,
        is_liked_by_auth_user: Boolean(comment.is_liked_by_auth_user),
      })) as Comment[];

      return pageFrom(comments, {
        total: response.meta.total,
        page: response.meta.current_page,
        limit: response.meta.per_page,
      });
    },
    [postId]
  );

  const { items, setItems, ...rest } = usePaginatedResource<Comment, {}>(
    fetcher,
    {
      enabled: Boolean(postId),
      resetKey: postId,
      errorMessage: "Failed to fetch comments",
    }
  );

  // Optimistic like toggling: the modal updates one row without refetching.
  const updateCommentInState = useCallback(
    (commentId: number, updater: (comment: Comment) => Comment) => {
      setItems((prev) =>
        prev.map((comment) =>
          comment.id === commentId ? updater(comment) : comment
        )
      );
    },
    [setItems]
  );

  return { comments: items, updateCommentInState, ...rest };
};

export const useCommentOperations = () => {
  const { run, runBoolean, setError, setLoading, ...rest } =
    useEntityOperations();

  const createComment = useCallback(
    (postId: number, commentData: CreateCommentInput) =>
      run(
        () => CommunityService.createComment(postId, commentData),
        "Failed to create comment"
      ),
    [run]
  );

  const deleteComment = useCallback(
    (postId: number, commentId: number) =>
      runBoolean(
        () => CommunityService.deleteComment(postId, commentId),
        "Failed to delete comment"
      ),
    [runBoolean]
  );

  // updateComment stays hand-rolled: unlike every other mutation here it
  // rethrows on failure, and the edit form depends on that to keep the
  // comment in edit mode.
  const updateComment = useCallback(
    async (postId: number, commentId: number, updates: UpdateCommentInput) => {
      setLoading(true);
      setError(null);

      try {
        const result = await CommunityService.updateComment(
          postId,
          commentId,
          updates
        );

        if (result.success) {
          return result.data;
        }

        throw new Error(result.error || "Failed to update comment");
      } catch (err: any) {
        const message =
          err?.message || "An unexpected error occurred while updating comment";
        setError(message);
        throw err instanceof Error ? err : new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [setError, setLoading]
  );

  const likeComment = useCallback(
    async (commentId: number) => {
      setError(null);

      try {
        const result = await CommunityService.likeComment(commentId);

        if (!result.success) {
          setError(result.error || "Failed to like comment");
        }

        return {
          success: result.success,
          error: result.error,
        };
      } catch (err) {
        setError("An unexpected error occurred while liking comment");
        return {
          success: false,
          error: "An unexpected error occurred while liking comment",
        };
      }
    },
    [setError]
  );

  const unlikeComment = useCallback(
    async (commentId: number) => {
      setError(null);

      try {
        const result = await CommunityService.unlikeComment(commentId);

        if (!result.success) {
          setError(result.error || "Failed to unlike comment");
        }

        return {
          success: result.success,
          error: result.error,
        };
      } catch (err) {
        setError("An unexpected error occurred while unliking comment");
        return {
          success: false,
          error: "An unexpected error occurred while unliking comment",
        };
      }
    },
    [setError]
  );

  return {
    createComment,
    updateComment,
    deleteComment,
    likeComment,
    unlikeComment,
    setError,
    setLoading,
    ...rest,
  };
};
